import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { api_urls } from "../../utilities";
import { getService } from "../../redux/services";

const initialState = {
  email: "",
  name: "",
  permissions: [],
  roles: [],
  /** Regular users (user_type=user) */
  users: [],
  usersLoading: false,
  usersLinks: null,
  /** Artists (user_type=artist) — includes full shop data */
  artists: [],
  artistsLoading: false,
  artistsLinks: null,
};

const buildFetchThunk = (typePrefix, userType) =>
  createAsyncThunk(typePrefix, async (params = {}, { rejectWithValue }) => {
    try {
      const response = await getService(api_urls.allUsers, {
        ...params,
        user_type: userType,
      });
      const envelope = response.data ?? {};
      const code = envelope.code ?? envelope.status;
      if (typeof code === "number" && code !== 200) {
        return rejectWithValue({ httpStatus: response.status, body: envelope });
      }
      const raw = envelope.data;
      return {
        list: Array.isArray(raw) ? raw : [],
        links: envelope.links ?? null,
      };
    } catch (error) {
      return rejectWithValue({
        httpStatus: error?.status,
        body: error?.data ?? { message: error?.message },
      });
    }
  });

export const fetchUsers = buildFetchThunk("user/fetchUsers", "user");
export const fetchArtists = buildFetchThunk("user/fetchArtists", "artist");

/** Legacy — kept for compatibility, fetches all without filter */
export const fetchAllUsers = createAsyncThunk(
  "user/fetchAllUsers",
  async (params = {}, { dispatch }) => {
    dispatch(fetchUsers(params));
    dispatch(fetchArtists(params));
  }
);

export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setUserInfo: (state, action) => {
      state.name = action.payload.name;
      state.email = action.payload.email;
      state.permissions = action.payload.permissions;
      state.roles = action.payload.roles;
    },
    unsetUserInfo: (state, action) => {
      state.name = action.payload.name;
      state.email = action.payload.email;
      state.permissions = action.payload.permissions;
      state.roles = action.payload.roles;
    },
    updateUsers: (state, action) => {
      state.users = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchUsers
      .addCase(fetchUsers.pending, (state) => {
        state.usersLoading = true;
      })
      .addCase(fetchUsers.fulfilled, (state, { payload }) => {
        state.usersLoading = false;
        state.users = payload.list;
        state.usersLinks = payload.links;
      })
      .addCase(fetchUsers.rejected, (state) => {
        state.usersLoading = false;
      })
      // fetchArtists
      .addCase(fetchArtists.pending, (state) => {
        state.artistsLoading = true;
      })
      .addCase(fetchArtists.fulfilled, (state, { payload }) => {
        state.artistsLoading = false;
        state.artists = payload.list;
        state.artistsLinks = payload.links;
      })
      .addCase(fetchArtists.rejected, (state) => {
        state.artistsLoading = false;
      });
  },
});

export const hasPermission = (permissionName) => (state) => {
  return state.user.permissions.some(
    (permission) => permission.name == permissionName
  );
};

export const { setUserInfo, unsetUserInfo, updateUsers } = userSlice.actions;

export default userSlice.reducer;
